const EMBED_SOURCE = 'classic-schedule';
const HOST_SOURCE = 'classic-schedule-host';

let frameHandle = null;
let shortRetryHandle = null;
let longRetryHandle = null;
let observerStarted = false;
let resizeObserver = null;
let mutationObserver = null;
let lastHeight = 0;
let observedRoot = null;

const clearRetryTimers = () => {
	if (shortRetryHandle) {
		window.clearTimeout(shortRetryHandle);
		shortRetryHandle = null;
	}

	if (longRetryHandle) {
		window.clearTimeout(longRetryHandle);
		longRetryHandle = null;
	}
};

const getQueryParams = () => new URLSearchParams(window.location.search || '');

export const isEmbeddedMode = () => {
	const params = getQueryParams();
	const value = (params.get('embedded') || '').toLowerCase();
	return value === 'true' || value === '1';
};

const getParentOrigin = () => {
	const params = getQueryParams();
	const value = params.get('parent_origin') || '';
	if (!value) {
		return '';
	}
	try {
		return new URL(value).origin;
	} catch (_error) {
		return '';
	}
};

const getMeasurementRoot = () => {
	const contentWrap = document.querySelector('#app .v-content__wrap');
	if (contentWrap) {
		return contentWrap;
	}

	const content = document.querySelector('#app .v-content');
	if (content) {
		return content;
	}

	const app = document.getElementById('app');
	if (!app) {
		return document.body;
	}
	return app.firstElementChild || app;
};

const measureHeight = () => {
	const root = getMeasurementRoot();
	if (!root) {
		return 0;
	}

	const rectHeight = Math.ceil(root.getBoundingClientRect().height || 0);
	const scrollHeight = Math.ceil(root.scrollHeight || 0);
	let childrenBottom = 0;

	if (root.children && root.children.length) {
		const rootTop = root.getBoundingClientRect().top;
		Array.prototype.forEach.call(root.children, child => {
			const rect = child.getBoundingClientRect();
			const bottom = Math.ceil(rect.bottom - rootTop);
			if (bottom > childrenBottom) {
				childrenBottom = bottom;
			}
		});
	}

	const contentHeight = Math.max(rectHeight, scrollHeight, childrenBottom);

	if (contentHeight > 0) {
		return Math.max(contentHeight, 240);
	}

	return Math.max(scrollHeight, 240);
};

const refreshObservers = () => {
	if (!observerStarted) {
		return;
	}

	const root = getMeasurementRoot();
	if (!root || root === observedRoot) {
		return;
	}

	observedRoot = root;

	if (mutationObserver) {
		mutationObserver.disconnect();
		mutationObserver.observe(root, {
			attributes: true,
			childList: true,
			subtree: true
		});
	}

	if (resizeObserver) {
		resizeObserver.disconnect();
		resizeObserver.observe(root);
	}
};

const postHeight = () => {
	if (!isEmbeddedMode() || window.parent === window) {
		return;
	}
	const targetOrigin = getParentOrigin();
	if (!targetOrigin) {
		return;
	}
	const nextHeight = measureHeight();

	if (!nextHeight || Math.abs(nextHeight - lastHeight) < 2) {
		return;
	}
	lastHeight = nextHeight;

	window.parent.postMessage(
		{
			source: EMBED_SOURCE,
			type: 'height-update',
			payload: { height: nextHeight }
		},
		targetOrigin
	);
};

export const scheduleHeightPost = () => {
	if (!isEmbeddedMode()) {
		return;
	}
	refreshObservers();
	clearRetryTimers();
	if (frameHandle) {
		window.cancelAnimationFrame(frameHandle);
	}
	frameHandle = window.requestAnimationFrame(() => {
		frameHandle = null;
		postHeight();
		shortRetryHandle = window.setTimeout(() => {
			shortRetryHandle = null;
			postHeight();
		}, 120);
		longRetryHandle = window.setTimeout(() => {
			longRetryHandle = null;
			postHeight();
		}, 320);
	});
};

export const setupEmbedBridge = () => {
	if (!isEmbeddedMode() || observerStarted) {
		return;
	}
	observerStarted = true;
	observedRoot = getMeasurementRoot();
	window.addEventListener('load', scheduleHeightPost);
	window.addEventListener('resize', scheduleHeightPost);
	window.addEventListener('message', event => {
		const targetOrigin = getParentOrigin();
		if (
			!targetOrigin ||
			event.origin !== targetOrigin ||
			event.source !== window.parent
		) {
			return;
		}
		const data =
			event.data && typeof event.data === 'object' ? event.data : null;
		if (
			!data ||
			data.source !== HOST_SOURCE ||
			data.type !== 'height-request'
		) {
			return;
		}
		scheduleHeightPost();
	});

	if (typeof MutationObserver !== 'undefined') {
		mutationObserver = new MutationObserver(scheduleHeightPost);
		mutationObserver.observe(observedRoot, {
			attributes: true,
			childList: true,
			subtree: true
		});
	}

	if (typeof ResizeObserver !== 'undefined') {
		resizeObserver = new ResizeObserver(scheduleHeightPost);
		resizeObserver.observe(observedRoot);
	}

	scheduleHeightPost();
};
